import { action, computed, makeObservable, observable } from 'mobx';
import type { RootStore } from '.';
import type { CardPriority } from '@creative-kit/shared';

/**
 * Store for managing the card priority assignment dialog state
 * Used primarily for bulk operations (multiple cards selected)
 */
export class UiCardPriorityStore {
  private rootStore: RootStore;

  // Dialog visibility
  isDialogOpen = false;
  targetCardIds: string[] = [];

  // Selected priority in dialog (null = no priority)
  selectedPriority: CardPriority | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      isDialogOpen: observable,
      targetCardIds: observable,
      selectedPriority: observable,
      isBatchMode: computed,
      targetCardCount: computed,
      commonPriority: computed,
      openDialog: action.bound,
      closeDialog: action.bound,
      setSelectedPriority: action.bound,
      saveAndClose: action.bound,
      cancelDialog: action.bound,
    });
  }

  get isBatchMode() {
    return this.targetCardIds.length > 1;
  }

  get targetCardCount() {
    return this.targetCardIds.length;
  }

  /**
   * Get the common priority if all target cards have the same priority
   * Returns undefined if priorities are mixed, null if all have no priority
   */
  get commonPriority(): CardPriority | null | undefined {
    if (this.targetCardIds.length === 0) return undefined;

    const { cards } = this.rootStore.cardsStore;
    const priorities = this.targetCardIds.map((id) => cards[id]?.data.priority ?? null);

    const first = priorities[0];
    const allSame = priorities.every((p) => p === first);

    return allSame ? first : undefined;
  }

  /**
   * Open the dialog for one or more cards
   */
  openDialog(cardIds: string | string[]) {
    const ids = Array.isArray(cardIds) ? cardIds : [cardIds];
    this.targetCardIds = ids;

    // Set initial selection to the common priority (if all cards have same)
    const common = this.commonPriority;
    this.selectedPriority = common === undefined ? null : common;

    this.isDialogOpen = true;
  }

  /**
   * Close the dialog without saving
   */
  closeDialog() {
    this.isDialogOpen = false;
    this.targetCardIds = [];
    this.selectedPriority = null;
  }

  /**
   * Set the selected priority
   */
  setSelectedPriority(priority: CardPriority | null) {
    this.selectedPriority = priority;
  }

  /**
   * Save the priority to Firestore and close dialog
   */
  async saveAndClose() {
    const { sessionCardActions, uiSnackbarStore } = this.rootStore;

    try {
      await sessionCardActions.batchSetCardPriority(this.targetCardIds, this.selectedPriority);

      const priorityLabel = this.selectedPriority
        ? this.selectedPriority.charAt(0).toUpperCase() + this.selectedPriority.slice(1)
        : 'None';

      uiSnackbarStore.setSnackbarDetails({
        description: `Updated priority to ${priorityLabel} for ${this.targetCardIds.length} cards`,
      });
    } catch (error) {
      console.error('Failed to save priority:', error);
      uiSnackbarStore.setSnackbarDetails({ description: 'Failed to save priority' });
    }

    this.closeDialog();
  }

  /**
   * Cancel all changes and close dialog
   */
  cancelDialog() {
    this.closeDialog();
  }
}
